import type { AuthTokens } from '@order-pilot/shared';
import type { TokenStore } from '@/lib/session';
import { saSession, tenantSession } from '@/lib/session';

const DEFAULT_API_URL = 'http://localhost:5070';

/** Base URL of the API, without a trailing slash. */
export const API_URL: string = (import.meta.env.VITE_API_URL ?? DEFAULT_API_URL).replace(/\/+$/, '');

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/** Every non-2xx response and every transport failure surfaces as this error. */
export class ApiError extends Error {
  /** HTTP status, or 0 when the request never reached the server. */
  readonly status: number;
  /** Parsed response body (or the underlying transport error), for debugging. */
  readonly details: unknown;

  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }

  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

export interface RequestOptions {
  method?: HttpMethod;
  /** JSON-serialised as the request body. */
  body?: unknown;
  /** Send the bearer token and refresh-and-retry once on 401. Default: true. */
  authenticated?: boolean;
  signal?: AbortSignal;
}

const SESSION_EXPIRED = 'Your session has expired. Please sign in again.';

function buildMessage(body: unknown, status: number): string {
  if (typeof body === 'string') {
    const text = body.trim();
    // Proxies and framework error pages return HTML — never show that raw.
    if (text && !text.startsWith('<')) return text.slice(0, 300);
  }
  if (body !== null && typeof body === 'object') {
    const { message, error } = body as { message?: unknown; error?: unknown };
    if (typeof message === 'string' && message.trim()) return message;
    if (Array.isArray(message)) {
      const parts = message.filter((part): part is string => typeof part === 'string');
      if (parts.length > 0) return parts.join(', ');
    }
    if (typeof error === 'string' && error.trim()) return error;
  }
  if (status === 401) return SESSION_EXPIRED;
  if (status === 403) return 'You are not allowed to perform this action.';
  if (status === 404) return 'Not found.';
  if (status >= 500) return 'The server ran into a problem. Please try again.';
  return `Request failed with status ${status}.`;
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  const body = await readBody(response);
  if (!response.ok) throw new ApiError(response.status, buildMessage(body, response.status), body);
  return body as T;
}

async function sendRequest(path: string, options: RequestOptions, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    return await fetch(`${API_URL}${path}`, {
      method: options.method ?? 'GET',
      headers,
      // Re-serialised on every attempt, so a refresh-retry can safely replay it.
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
      credentials: 'omit',
      mode: 'cors',
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'Cannot reach the server. Check your connection and try again.', error);
  }
}

/**
 * Multipart is its own path rather than a `sendRequest` body type, because
 * that function always JSON-serialises and always sets `Content-Type:
 * application/json` when a body is present — both wrong for a file upload.
 * `Content-Type` is deliberately left UNSET here: the browser fills in
 * `multipart/form-data; boundary=...` itself only when it owns setting the
 * header, and a manually-set one without the boundary breaks parsing
 * server-side.
 */
async function sendMultipart(
  path: string,
  method: 'POST' | 'PUT',
  formData: FormData,
  token: string | null,
  signal?: AbortSignal,
): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    return await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: formData,
      signal,
      credentials: 'omit',
      mode: 'cors',
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'Cannot reach the server. Check your connection and try again.', error);
  }
}

/**
 * True only when the server actively rejected the refresh token. A network
 * failure, a 404 from a misconfigured URL or a 5xx is not a verdict on the
 * token, so the session must survive those.
 */
function isSessionRejected(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

type BodylessOptions = Omit<RequestOptions, 'method' | 'body'>;
export type { BodylessOptions };

export interface ApiClient {
  request<T>(path: string, options?: RequestOptions): Promise<T>;
  get<T>(path: string, options?: BodylessOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: BodylessOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: BodylessOptions): Promise<T>;
  delete<T>(path: string, options?: BodylessOptions): Promise<T>;
  /** Multipart upload — see `sendMultipart` for why this can't reuse `request`. */
  uploadFile<T>(
    path: string,
    method: 'POST' | 'PUT',
    formData: FormData,
    options?: BodylessOptions,
  ): Promise<T>;
}

export interface ApiClientConfig {
  /** The token store this client — and only this client — reads and writes. */
  tokens: TokenStore;
  /** Audience-specific refresh endpoint, e.g. `/admin/refresh`. */
  refreshPath: string;
}

/**
 * Builds a client bound to ONE session. Each portal gets its own instance, so
 * the bearer token, the refresh endpoint and the single-flight refresh state
 * are all scoped to a single JWT audience — no cross-portal token reuse is
 * even expressible.
 */
export function createApiClient({ tokens, refreshPath }: ApiClientConfig): ApiClient {
  /**
   * Single-flight refresh: concurrent 401s all await the same in-flight call,
   * so a report page with several queries never stampedes the refresh endpoint.
   */
  let refreshInFlight: Promise<string> | null = null;

  async function performRefresh(): Promise<string> {
    const refreshToken = tokens.getRefreshToken();
    if (!refreshToken) throw new ApiError(401, SESSION_EXPIRED);
    const response = await sendRequest(refreshPath, { method: 'POST', body: { refreshToken } }, null);
    const rotated = await parseResponse<AuthTokens>(response);
    if (!rotated || typeof rotated.accessToken !== 'string' || typeof rotated.refreshToken !== 'string') {
      throw new ApiError(401, SESSION_EXPIRED, rotated);
    }
    tokens.setTokens(rotated);
    return rotated.accessToken;
  }

  function refreshAccessToken(): Promise<string> {
    if (refreshInFlight === null) {
      const attempt = performRefresh();
      refreshInFlight = attempt;
      void attempt
        .catch((error: unknown) => {
          // The session is over: drop it once, here. The matching auth context
          // observes the cleared session and its route guard sends the user to
          // that portal's login page.
          if (isSessionRejected(error)) tokens.clear();
        })
        .finally(() => {
          refreshInFlight = null;
        });
    }
    return refreshInFlight;
  }

  /** Refreshes, normalising "the session is gone" into a 401 for callers. */
  async function ensureAccessToken(): Promise<string> {
    try {
      return await refreshAccessToken();
    } catch (error) {
      if (isSessionRejected(error)) throw new ApiError(401, SESSION_EXPIRED, error);
      throw error;
    }
  }

  /** Typed JSON request with transparent one-shot token refresh on 401. */
  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const authenticated = options.authenticated ?? true;
    let token: string | null = null;

    if (authenticated) {
      token = tokens.getAccessToken();
      // After a reload the access token is gone but the refresh token survives.
      if (token === null && tokens.getRefreshToken() !== null) {
        token = await ensureAccessToken();
      }
    }

    let response = await sendRequest(path, options, token);

    if (response.status === 401 && authenticated) {
      const freshToken = await ensureAccessToken();
      // Exactly one retry: a second 401 propagates to the caller.
      response = await sendRequest(path, options, freshToken);
    }

    return parseResponse<T>(response);
  }

  /** Multipart counterpart to `request` — same auth/refresh-on-401 flow. */
  async function uploadFile<T>(
    path: string,
    method: 'POST' | 'PUT',
    formData: FormData,
    options: BodylessOptions = {},
  ): Promise<T> {
    const authenticated = options.authenticated ?? true;
    let token: string | null = null;

    if (authenticated) {
      token = tokens.getAccessToken();
      if (token === null && tokens.getRefreshToken() !== null) {
        token = await ensureAccessToken();
      }
    }

    let response = await sendMultipart(path, method, formData, token, options.signal);

    if (response.status === 401 && authenticated) {
      const freshToken = await ensureAccessToken();
      response = await sendMultipart(path, method, formData, freshToken, options.signal);
    }

    return parseResponse<T>(response);
  }

  return {
    request,
    uploadFile,
    get: <T>(path: string, options: BodylessOptions = {}) => request<T>(path, { ...options, method: 'GET' }),
    post: <T>(path: string, body?: unknown, options: BodylessOptions = {}) =>
      request<T>(path, { ...options, method: 'POST', body }),
    patch: <T>(path: string, body?: unknown, options: BodylessOptions = {}) =>
      request<T>(path, { ...options, method: 'PATCH', body }),
    delete: <T>(path: string, options: BodylessOptions = {}) => request<T>(path, { ...options, method: 'DELETE' }),
  };
}

/** Super-admin client: `aud: sa` tokens, /admin/* endpoints. */
export const api: ApiClient = createApiClient({ tokens: saSession, refreshPath: '/admin/refresh' });

/** Tenant-portal client: `aud: tenant` tokens, /portal/* endpoints. */
export const portalApi: ApiClient = createApiClient({ tokens: tenantSession, refreshPath: '/portal/refresh' });

/** Query-string builder that drops empty values. */
export function queryString(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

export function getErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error.trim()) return error;
  return fallback;
}
